/**
 * Marketing + utility page content registry.
 * Each entry drives ContentPageTemplate or a specialized page component.
 */

export interface ContentSection {
  heading?: string;
  intro?: string;
  paragraphs?: string[];
  bullets?: string[];
  type?: 'prose' | 'twoCol' | 'cards' | 'numberedList';
}

export interface MarketingPage {
  slug: string;
  url: string;
  metaTitle: string;
  metaDescription: string;
  heroEyebrow: string;
  heroHeadline: string;
  heroHeadlineAccent?: string;
  heroLead: string;
  sections: ContentSection[];
  cta?: { headline: string; body: string };
  schemaType?: 'AboutPage' | 'ContactPage' | 'WebPage' | 'CollectionPage' | 'FAQPage';
}

export const marketingPages: Record<string, MarketingPage> = {
  'about-us': {
    slug: 'about-us',
    url: '/about-us/',
    schemaType: 'AboutPage',
    metaTitle: 'About Morgan Legal Group — NYC Estate Planning & Probate Attorneys',
    metaDescription:
      'Morgan Legal Group, P.C. — a New York City law firm for estate planning, probate, elder law, family law and real estate. Founded 2017 by Russel Morgan, Esq.',
    heroEyebrow: 'About The Firm',
    heroHeadline: 'A NYC Law Firm Built Around',
    heroHeadlineAccent: 'Your Family',
    heroLead:
      "Morgan Legal Group was founded in 2017 with a clear premise: New York families deserve a single trusted firm that handles every chapter of their legal life — estate planning, probate, elder care, family matters, and real estate. Today the firm serves clients across New York State.",
    sections: [
      {
        heading: 'Our Mission',
        paragraphs: [
          "Morgan Legal Group exists to help New York families face legal decisions with clarity, dignity, and a measurable result. We focus on the practice areas where one wrong document or one missed deadline costs a family its home, its inheritance, or its dignity — and we work to make sure that does not happen on our watch.",
          "We are not a referral mill. We are not a volume firm. Every engagement is staffed with a senior attorney who personally reviews the work, supported by an associate team that handles execution and follow-through.",
        ],
      },
      {
        heading: 'Practice Areas',
        intro: 'The firm handles eleven principal practice areas across estate and family law work:',
        bullets: [
          'Estate Planning — wills, trusts, Medicaid asset protection, powers of attorney',
          "Probate — Surrogate's Court administration, contests, and litigation",
          'Wills & Trusts — drafting, funding, special-needs and pooled-income trusts',
          'Asset Protection — MAPT, LLC structuring, judgment-proofing strategies',
          'Guardianship — Article 81 (MHL) and Article 17-A (SCPA) proceedings',
          'Power of Attorney — statutory short form (GOL § 5-1501B), health care proxies',
          'Elder Law — Medicaid planning, long-term care, elder abuse defense',
          'Family Law — custody, support, paternity, orders of protection',
          'Matrimonial — contested and uncontested divorce, equitable distribution',
          'Real Estate — residential and commercial closings, title disputes',
          'Personal Injury — serious injury claims under Insurance Law § 5102',
        ],
      },
      {
        heading: 'Founding Partner',
        paragraphs: [
          "Russel Morgan, Esq., founded Morgan Legal Group after completing his JD at New York Law School and advanced study at Lloyd's of London. He is admitted to practice in New York and is fluent in Russian, serving a substantial Russian-speaking client base across the metropolitan area.",
          "Russel personally oversees every estate-planning engagement and every contested Surrogate's Court matter at the firm.",
        ],
      },
      {
        heading: 'How We Work',
        intro: 'Every new engagement follows the same disciplined process:',
        bullets: [
          'Initial consultation — free, in-person or by video, with a senior attorney',
          'Scope letter — written engagement agreement with flat fees where possible',
          'Document gathering — secure portal access for asset, family, and beneficiary information',
          'Drafting — preliminary drafts reviewed in person before execution',
          'Execution — attorney-supervised signing with witnesses and notarization',
          'Storage — originals returned, digital copies retained for life',
          'Follow-up — annual review reminder, free update consultation for life events',
        ],
      },
    ],
    cta: {
      headline: 'Meet Morgan Legal Group',
      body: 'Schedule a free consultation to talk through what you need. We will tell you exactly what we can do, what it costs, and whether we are the right firm for the matter.',
    },
  },

  'our-fees': {
    slug: 'our-fees',
    url: '/our-fees/',
    metaTitle: 'Flat-Fee Estate Planning Lawyer NYC — Our Fees | Morgan Legal',
    metaDescription:
      'Flat fees for New York estate planning and clearly scoped hourly rates for litigation, explained up front. Free initial consultation.',
    heroEyebrow: 'Fee Structure',
    heroHeadline: 'Transparent Fees,',
    heroHeadlineAccent: 'No Surprises',
    heroLead:
      "We publish our fee structure openly because we believe the cost of legal help is part of what makes legal help accessible. Most estate-planning work is flat-fee. Contingency matters are no-fee-unless-we-win. Litigation hourly work comes with a written budget.",
    sections: [
      {
        heading: 'Free Consultation',
        paragraphs: [
          'Every new client matter starts with a free initial consultation. This is a working meeting — not a sales pitch — where a senior attorney reviews your situation, identifies the legal issues, and tells you what the firm can and cannot do.',
          'You leave the consultation with a clear next step, whether or not you retain Morgan Legal Group.',
        ],
      },
      {
        heading: 'Flat Fees — Estate Planning',
        intro: 'Most estate planning matters are flat-fee. The fee is quoted in writing before any work begins.',
        bullets: [
          'Simple Last Will & Testament — flat fee, executed with self-proving affidavit',
          'Comprehensive Estate Plan (will + POA + health care proxy + living will) — flat fee bundle',
          'Revocable Living Trust + pour-over will package — flat fee, includes funding consultation',
          'Medicaid Asset Protection Trust (MAPT) — flat fee, includes five-year look-back planning',
          'Special Needs Trust (1st-party or 3rd-party) — flat fee, with annual administration option',
          'Pooled Income Trust enrollment — flat fee, includes coordination with sponsoring nonprofit',
        ],
      },
      {
        heading: "Hourly + Court Costs — Probate & Litigation",
        intro: 'Probate administration and contested matters are typically hourly, with a written budget at every engagement.',
        bullets: [
          "Uncontested probate (full SCPA § 1402 admission to distribution) — hourly with budget cap, plus court filing fees scaled to estate size per SCPA § 2402",
          'Contested probate / will contest — hourly with budget reviewed quarterly',
          'Article 81 guardianship — hourly, with separate billing for evaluator fees and court costs',
          'Surrogate accounting proceedings — hourly with budget cap',
          'Real estate litigation — hourly with retainer',
        ],
      },
      {
        heading: 'Contingency Fees — Personal Injury',
        paragraphs: [
          'Personal injury cases are handled on contingency: no fee unless we win.',
          'Standard contingency is one-third of any recovery, with statutory adjustments where applicable. Court costs and expert witness fees are advanced by the firm and reimbursed from the recovery.',
        ],
      },
      {
        heading: 'Payment Options',
        bullets: [
          'Major credit cards accepted',
          'ACH bank transfer for larger retainers',
          'Payment plans available on estate-planning engagements with prior approval',
          'IOLTA trust account for retainers per New York Rules of Professional Conduct',
        ],
      },
    ],
    cta: {
      headline: 'Ask About Your Specific Matter',
      body: 'Every fee on this page is published as a guideline. The actual quote depends on your specific facts and is provided in writing at the start of every engagement.',
    },
  },

  reviews: {
    slug: 'reviews',
    url: '/reviews/',
    metaTitle: 'Client Reviews & Video Testimonials — Morgan Legal Group NYC',
    metaDescription:
      'Client reviews and video testimonials for Morgan Legal Group, P.C. — estate planning, probate, elder law and family law attorneys in New York City.',
    heroEyebrow: 'Client Reviews & Video Testimonials',
    heroHeadline: 'What Our',
    heroHeadlineAccent: 'Clients Say',
    heroLead:
      // 🔴 Было «maintains 5-star ratings across …». Сайт в пяти других местах
      // называет 4.9 — и в тексте, и в schema.org. Две машиночитаемые величины
      // на одном сайте противоречили друг другу, а «5-star» вдобавок
      // округляло вверх. Ставим ту же цифру, что и везде.
      "Morgan Legal Group holds a 4.9 average client rating across Google, Avvo and Justia. The reviews below are excerpted from those platforms; full reviews can be read on the original platforms via the links.",
    sections: [
      {
        heading: 'Estate Planning Clients',
        paragraphs: [
          '"Russel walked us through a Medicaid trust we never knew existed. Saved my mother\'s home from being eaten by long-term care costs. Worth every penny." — D.K., Brooklyn',
          '"The flat fee was honored exactly as quoted. Our trust was funded properly the first time. Comparing notes with friends who used other firms, our experience was qualitatively better." — M.S., Manhattan',
          '"After my husband\'s diagnosis, Morgan Legal Group got our estate plan and POAs in place within two weeks. They understood the urgency." — R.P., Queens',
        ],
      },
      {
        heading: 'Probate Clients',
        paragraphs: [
          '"Our father\'s will contest was settled before trial because Morgan Legal Group had built the record from day one. Other firms had told us to just settle and walk away." — A.L., Bronx',
          '"Letters Testamentary in five weeks for an estate with assets in three counties. We did not know that was even possible." — J.W., Staten Island',
        ],
      },
      {
        heading: 'Family Law Clients',
        paragraphs: [
          '"Joel handled my divorce with discipline and zero drama. The equitable distribution worked out better than I expected." — N.T., Manhattan',
          '"Sofia was assigned to our Article 81 guardianship for my aunt. We needed someone patient with our family and rigorous with the court. She was both." — H.G., Brooklyn',
        ],
      },
      {
        heading: 'Video Testimonials',
        paragraphs: [
          'Video testimonials from clients who have worked with Morgan Legal Group are published on the firm\'s YouTube channel. Video content is added periodically as clients consent on camera; the most recent uploads are linked from the publications page.',
          'The video testimonials are embedded below. More are on our YouTube channel. (If you are a past client willing to record a short video, please contact us — written and video testimonials remain entirely voluntary and clients are never compensated.)',
        ],
      },
      {
        heading: 'Where to Leave a Review',
        intro: 'If you are a current or past client, please consider leaving a review on:',
        bullets: [
          'Google — search "Morgan Legal Group NYC"',
          'Avvo — avvo.com/attorneys/10038-ny-russel-morgan',
          'Justia — justia.com profile',
          'Yelp — yelp.com listing',
        ],
      },
    ],
    // 🔴 Прежний заголовок — «Become Our Next 5-Star Review» — просил у клиента
    // не отзыв, а ЗВЁЗДЫ: обещал результат («пятизвёздочный») и склонял к
    // конкретной оценке. Это запрещено правилами площадок отзывов и создаёт
    // необоснованное ожидание в адвокатской рекламе. Призыв должен звать на
    // консультацию, а не на оценку.
    cta: {
      headline: 'Schedule Your Consultation',
      body: 'Tell us about your matter. A senior attorney will review it with you.',
    },
  },

  'contact-us': {
    slug: 'contact-us',
    url: '/contact-us/',
    schemaType: 'ContactPage',
    metaTitle: 'Contact Estate Planning Attorneys — NYC, Brooklyn, Queens',
    metaDescription:
      "Morgan Legal Group, P.C. — 15 Maiden Lane, Suite 905, Manhattan. Call (888) 529-1315, email contact@morganlegalny.com, or book online.",
    heroEyebrow: 'Get In Touch',
    heroHeadline: "Let's Talk About",
    heroHeadlineAccent: 'Your Matter',
    heroLead:
      "Every new matter starts with a free, no-obligation consultation. Reach us by phone, email, secure online form, or in person at our Maiden Lane office in downtown Manhattan.",
    sections: [
      {
        heading: 'Office',
        paragraphs: [
          '15 Maiden Lane, Suite 905',
          'New York, NY 10038',
          '(Two blocks from Fulton Street subway; A, C, J, Z, 2, 3, 4, 5 lines all stop within a five-minute walk.)',
        ],
      },
      {
        heading: 'Phone',
        paragraphs: [
          'New clients: 888-529-1315 (toll-free — the fastest way to reach an attorney)',
          'Existing matters / office line: 212-561-4299',
          'Phone hours: Monday–Friday, 9:00 AM – 6:00 PM Eastern. After-hours messages return the next business day.',
        ],
      },
      {
        heading: 'Email',
        paragraphs: [
          'contact@morganlegalny.com — staffed Monday through Friday during business hours. We respond to most emails within four business hours.',
        ],
      },
      {
        heading: 'Schedule a Consultation',
        paragraphs: [
          'Use the Schedule Free Consultation button below to book a video or in-person meeting. The booking system shows live availability and confirms your slot instantly.',
        ],
      },
      {
        heading: 'Important Disclaimer',
        paragraphs: [
          'Contacting Morgan Legal Group through this website does not create an attorney-client relationship. Please do not send confidential information through unencrypted email or the contact form. An attorney-client relationship is established only by a signed engagement letter.',
        ],
      },
    ],
    cta: {
      headline: 'Ready When You Are',
      body: 'Phone, email, online — whatever works for your schedule. We will respond within four business hours.',
    },
  },

  appointment: {
    slug: 'appointment',
    url: '/appointment/',
    metaTitle: 'Book a Free Consultation | NYC Estate Planning Attorney',
    metaDescription:
      'Schedule a free consultation with Morgan Legal Group. Video or in-person, 30-60 minutes, with a senior attorney. No obligation. 888-529-1315 or book online.',
    heroEyebrow: 'Free Consultation',
    heroHeadline: 'Schedule Your',
    heroHeadlineAccent: 'Free Consultation',
    heroLead:
      "Every new matter begins with a free consultation. Bring your questions and any relevant documents. We will tell you what the firm can do, how much it costs, and whether Morgan Legal Group is the right fit.",
    sections: [
      {
        heading: 'What To Expect',
        intro: 'The consultation is 30–60 minutes with a senior attorney. We cover:',
        bullets: [
          'Your situation — facts, timeline, and what you want to accomplish',
          'Legal options — what the law allows and what we recommend',
          'Engagement scope — exactly what we would do for you',
          'Fees — written quote, flat-fee where possible, hourly with cap where not',
          'Next steps — a clear "yes, retain" or "no, here is what to do instead"',
        ],
      },
      {
        heading: 'Three Ways to Book',
        intro: 'Use whichever is easiest:',
        bullets: [
          'Call 888-529-1315 — same-day or next-day slots usually available',
          'Email contact@morganlegalny.com — include 2-3 times that work for you',
          'Click the Schedule Free Consultation button below to use our online calendar',
        ],
      },
      {
        heading: 'Video or In-Person',
        paragraphs: [
          'Most consultations are now video (Zoom or Google Meet) for client convenience. In-person consultations at our Maiden Lane office in Manhattan are available on request.',
          'For estate planning matters with significant real estate or business assets, an in-person meeting is often more productive.',
        ],
      },
      {
        heading: 'What To Bring',
        intro: 'Helpful (not required) for an effective consultation:',
        bullets: [
          'Estate planning — list of major assets, current will/trust documents if any, names of intended beneficiaries',
          'Probate — death certificate, original will (if any), list of estate assets',
          'Family law — separation date, list of joint assets, prior court orders',
          'Real estate — contract of sale, title report if available',
          'Personal injury — police report, medical records, photos of injuries/scene',
        ],
      },
    ],
    cta: {
      headline: 'Book Now',
      body: 'Free consultation. No obligation. 30-60 minutes with a senior attorney.',
    },
  },

  publications: {
    slug: 'publications',
    url: '/publications/',
    metaTitle: 'Publications & Media | Morgan Legal Group NYC',
    metaDescription:
      'Articles, press features, and educational publications by Morgan Legal Group on estate planning, probate, elder law, and family law in New York.',
    heroEyebrow: 'Publications',
    heroHeadline: 'Articles, Press,',
    heroHeadlineAccent: '& Education',
    heroLead:
      "Morgan Legal Group contributes to the public legal record through articles, podcast appearances, and educational publications. The materials below are a selection of what is available on the firm's blog at morganlegalny.com.",
    sections: [
      {
        heading: 'Featured Topics',
        intro: "Recurring topics in the firm's publications:",
        bullets: [
          'Medicaid asset protection — the 60-month look-back and how to plan around it',
          'New York will contests — SCPA § 1404 strategy and timing',
          'Article 81 guardianship — Mental Hygiene Law and practical realities',
          'Special needs planning — first-party vs. third-party SNT design',
          'Power of attorney — why the New York statutory short form matters',
          'Real estate co-op probate — what makes co-op estate transfer different',
        ],
      },
      {
        heading: 'The Blog',
        paragraphs: [
          'The firm maintains an active blog at morganlegalny.com with over 4,000 articles spanning estate planning, probate, elder law, family law, real estate, and personal injury — written and reviewed by the firm\'s attorneys.',
        ],
      },
      {
        heading: 'Podcast: Obsessed With Success',
        paragraphs: [
          "Russel Morgan hosts the firm's podcast, Obsessed With Success, where attorneys, founders, and operators discuss what it takes to build something durable. Available on Spotify, Apple Podcasts, and the firm's website.",
        ],
      },
      {
        heading: 'Speaking & Press',
        paragraphs: [
          'Russel Morgan and Morgan Legal Group attorneys speak regularly at NYSBA continuing legal education events, community estate planning seminars, and Russian-American Bar Association meetings.',
        ],
      },
    ],
  },

  resources: {
    slug: 'resources',
    url: '/resources/',
    metaTitle: 'Legal Resources for NYC Families | Morgan Legal Group',
    metaDescription:
      'Free legal resources from Morgan Legal Group: estate planning checklists, Medicaid eligibility guides, probate timelines, glossary of legal terms, and FAQ.',
    heroEyebrow: 'Free Resources',
    heroHeadline: 'Tools To Help You',
    heroHeadlineAccent: 'Start',
    heroLead:
      "We publish working tools — not marketing material — that help families think through legal decisions before, during, and after retaining counsel. Use what is helpful; ignore what is not.",
    sections: [
      {
        heading: 'Estate Planning',
        bullets: [
          'Asset inventory worksheet — list everything that needs to pass to beneficiaries',
          'Beneficiary questionnaire — gather what your attorney will ask',
          'Document checklist — what to bring to your first estate planning consultation',
          'Annual estate plan review checklist — life events that should trigger an update',
        ],
      },
      {
        heading: 'Probate',
        bullets: [
          "Executor's first 30 days — what must happen and when",
          'Probate timeline calculator — typical durations by county and estate complexity',
          'Creditor notice requirements — SCPA § 1801 publication schedule',
          'Estate accounting templates — informal and judicial accounting',
        ],
      },
      {
        heading: 'Medicaid & Elder Law',
        bullets: [
          'Medicaid income/resource limits — 2026 New York figures',
          '60-month look-back rule — what counts and what does not',
          'Medicaid asset protection trust (MAPT) overview',
          'Long-term care cost calculator — NYC market rates',
        ],
      },
      {
        heading: 'Glossary & FAQ',
        paragraphs: [
          'See our /glossary-of-legal-terms/ page for plain-English definitions of every term in estate, probate, elder law, and family law work. Each practice-area hub page also has a 10-question FAQ specific to that practice.',
        ],
      },
    ],
  },

  'practice-areas': {
    slug: 'practice-areas',
    url: '/practice-areas/',
    schemaType: 'CollectionPage',
    metaTitle: 'Practice Areas | Morgan Legal Group NYC',
    metaDescription:
      "Morgan Legal Group's New York practice areas: estate planning, probate, wills and trusts, asset protection, guardianship, elder law, family law, real estate.",
    heroEyebrow: 'Twelve Practice Areas',
    heroHeadline: 'Every Major',
    heroHeadlineAccent: 'NYC Legal Issue',
    heroLead:
      "Morgan Legal Group is a full-service firm. Below are the twelve principal practice areas we cover, each with a dedicated hub page that explains New York statutes, the typical process, common questions, and how we work.",
    // Список разделов переехал в src/pages/marketing/PracticeAreasHub.tsx:
    // страница рисуется своим компонентом (сетка карточек), а не обобщённым
    // ContentPageTemplate. Оставлять здесь второй, расходящийся с ней перечень
    // значит завести ловушку для следующей правки — она попала бы в данные,
    // которые никто не отображает.
    sections: [],
    cta: {
      headline: 'Not Sure Which Practice Area Fits?',
      body: 'Call us. Every case is different and most matters touch more than one practice area. Free consultation, no obligation.',
    },
  },
};
